import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const roleRoute = (role) => {
    if (role === "superadmin") return "/admin";
    if (role === "instructor") return "/instructor";
    return "/simulations";
  };

  useEffect(() => {
    if (user) {
      navigate(roleRoute(user.role));
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success(t("auth.welcomeBack", { name: userData.name }));
      navigate(roleRoute(userData.role));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("auth.invalidCredentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg relative overflow-hidden">
      <div className="hero-overlay absolute inset-0" />
      <div className="absolute top-5 right-5 z-20 flex items-center gap-2">
        <ThemeToggle variant="login" />
        <LanguageSwitcher variant="login" />
      </div>
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-[420px] animate-fade-in-up">
          <div className="text-center mb-10">
            <img src="/logo-medsim.png" alt="MedSim" className="h-[185px] mx-auto mb-2" />
          </div>
          <Card className="border border-slate-200/80 shadow-2xl shadow-black/10">
            <CardContent className="p-8">
              <div className="mb-6">
                <h2 className="text-xl font-heading font-medium text-slate-900">{t("auth.login")}</h2>
                <p className="text-sm text-slate-500 mt-1">{t("auth.loginSubtitle")}</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auth.email")}</Label>
                  <Input id="email" type="email" placeholder="nombre@universidad.edu" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 border-slate-300 focus:border-slate-500 focus:ring-slate-400" data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auth.password")}</Label>
                  <Input id="password" type="password" placeholder="********" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 border-slate-300 focus:border-slate-500 focus:ring-slate-400" data-testid="login-password-input" />
                </div>
                <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-md shadow-sm transition-all duration-200" disabled={loading} data-testid="login-submit-btn">
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("common.loading")}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">{t("auth.login")}<ArrowRight className="w-4 h-4" /></span>
                  )}
                </Button>
              </form>
              <div className="mt-8 pt-6 border-t border-slate-100 text-center text-sm text-slate-500">
                {t("auth.noAccount")}{" "}
                <Link to="/register" className="text-slate-900 hover:text-slate-700 font-semibold" data-testid="register-link">{t("auth.register")}</Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
